import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState } from '@ui-kit';
import { useConfigChanges } from '../hooks/useConfigChanges';
import type { ApiError, ChangeType } from '../types/configCenter';

const CHANGE_TYPE_TONE: Record<ChangeType, string> = {
  CREATED: 'var(--color-success, #22c55e)',
  UPDATED: 'var(--color-brand-primary, #6200ee)',
  DELETED: 'var(--color-danger, #ef4444)',
};

interface ConfigChangeFeedDrawerProps {
  onClose: () => void;
}

/**
 * ConfigChangeFeedDrawer — 변경 피드 우측 drawer (dialog 상태)
 * role=dialog · aria-modal=true · ESC 닫기
 * 30초 폴링으로 자동 갱신 (C085 ConfigChangedEvent 구독 결과 시연)
 */
export function ConfigChangeFeedDrawer({ onClose }: ConfigChangeFeedDrawerProps) {
  const { t } = useTranslation();

  const changesQuery = useConfigChanges(
    { page: 0, size: 50, sort: 'changedAt,desc' },
    true
  );

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const items = changesQuery.data?.content ?? [];

  function formatTime(ts: string): string {
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return ts;
    }
  }

  function formatDate(ts: string): string {
    try {
      const d = new Date(ts);
      const today = new Date();
      if (
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
      ) {
        return '';
      }
      return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  function renderValue(
    changeType: ChangeType,
    sensitive: boolean,
    oldValue: string | null,
    newValue: string | null
  ): string {
    if (sensitive) return t('configCenter.changes.sensitiveMasked');
    if (changeType === 'CREATED') {
      return `${t('configCenter.changes.created')}  ${newValue ?? ''}`;
    }
    if (changeType === 'DELETED') {
      return `${oldValue ?? ''} → (삭제)`;
    }
    return `${oldValue ?? ''} → ${newValue ?? ''}`;
  }

  return (
    <>
      {/* backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 900,
          background: 'rgba(0,0,0,0.3)',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="changes-drawer-title"
        aria-label={t('configCenter.changes.closeAriaLabel')}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 901,
          width: 420,
          maxWidth: '100vw',
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
          }}
        >
          <div>
            <h2
              id="changes-drawer-title"
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              {t('configCenter.changes.title')}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                margin: 'var(--spacing-1) 0 0',
              }}
            >
              {t('configCenter.changes.subtitle')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => void changesQuery.refetch()}
              disabled={changesQuery.isFetching}
              aria-label={t('configCenter.changes.refresh')}
              style={{
                background: 'transparent',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--spacing-1) var(--spacing-2)',
                fontSize: 'var(--font-size-xs)',
                cursor: changesQuery.isFetching ? 'not-allowed' : 'pointer',
                color: 'var(--color-text-secondary)',
                opacity: changesQuery.isFetching ? 0.6 : 1,
              }}
            >
              {changesQuery.isFetching ? '...' : '↺'}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('configCenter.changes.closeAriaLabel')}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 'var(--font-size-lg)',
                cursor: 'pointer',
                color: 'var(--color-text-muted)',
                lineHeight: 1,
                padding: 'var(--spacing-1)',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* 컨텐츠 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-3) var(--spacing-4)' }}>
          {/* loading skeleton */}
          {changesQuery.isLoading && (
            <div
              aria-label={t('configCenter.changes.loadingAriaLabel')}
              aria-busy="true"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 56,
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-surface-elevated)',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
          )}

          {/* error */}
          {changesQuery.isError && (
            <ErrorState
              title={t('configCenter.error.loadFailed')}
              message={
                (changesQuery.error as unknown as ApiError)?.message ??
                t('configCenter.error.network')
              }
              onRetry={() => void changesQuery.refetch()}
            />
          )}

          {/* empty */}
          {!changesQuery.isLoading && !changesQuery.isError && items.length === 0 && (
            <EmptyState
              title={t('configCenter.changes.empty.title')}
              description={t('configCenter.changes.empty.description')}
            />
          )}

          {/* 피드 목록 */}
          {!changesQuery.isLoading && !changesQuery.isError && items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
              {items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    padding: 'var(--spacing-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-surface-border)',
                    background: 'var(--color-surface-elevated)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                        fontFamily: 'var(--font-family-mono)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {item.configKey}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: CHANGE_TYPE_TONE[item.changeType],
                        fontWeight: 600,
                        marginLeft: 'var(--spacing-2)',
                        flexShrink: 0,
                      }}
                    >
                      {t(`configCenter.changes.type.${item.changeType}`)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    {renderValue(item.changeType, item.sensitive, item.oldValue, item.newValue)}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    <span>
                      {formatDate(item.changedAt) && `${formatDate(item.changedAt)} `}
                      {formatTime(item.changedAt)}
                    </span>
                    {item.changedBy && <span>· {item.changedBy}</span>}
                    {item.sensitive && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-warning, #f59e0b)',
                          fontWeight: 600,
                        }}
                      >
                        ● {t('configCenter.sensitive.yes')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <span aria-hidden="true">ⓘ</span>
          <span>ConfigChangedEvent (C085) 구독 결과 · 30초마다 자동 갱신</span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
